import { useEffect, useRef, useState } from 'react';
import { insertTokenAt } from './blockTree';

/**
 * Small popover button that splices a `{{token}}` into a text input or
 * textarea at the current caret position. Wired up by the SettingsPanel
 * next to every text field that accepts merge tokens.
 *
 * Tokens are intentionally low-curation — the builder shows the recruiter
 * what the active sample resolves but doesn't block them from inserting
 * an unknown token. A warning chip surfaces unknown tokens elsewhere in
 * the UI.
 */

export interface MergeFieldInserterProps {
  /** Tokens to show in the popover, in order. `readonly` so callers can
   *  pass a `readonly string[]` literal (e.g. `COMMON_MERGE_TOKENS`)
   *  without spreading or casting. */
  tokens: readonly string[];
  /** Called with the new value (the original value with a {{token}} spliced
   *  in at the caret position) when the user picks a token. */
  onInsert: (nextValue: string) => void;
  /** Source-of-truth value the popover splices into. */
  value: string;
  /** Caret position to splice at — typically the input's selectionStart at
   *  the moment the popover button was clicked. */
  caret: number;
  /** Aria label for the open button. */
  label?: string;
}

export function MergeFieldInserter({
  tokens,
  onInsert,
  value,
  caret,
  label = 'Insert merge field',
}: MergeFieldInserterProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!popoverRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={popoverRef}>
      <button
        type="button"
        aria-label={label}
        aria-expanded={open}
        data-testid="merge-inserter-open"
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: '2px 8px',
          fontSize: 11,
          fontWeight: 700,
          border: '1px solid var(--color-border-default, #ececf2)',
          borderRadius: 4,
          background: '#fff',
          color: 'var(--color-text-muted, #6b6b7d)',
          cursor: 'pointer',
          fontFamily: 'inherit',
        }}
      >
        {'{ }'}
      </button>
      {open && (
        <div
          role="menu"
          data-testid="merge-inserter-menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            zIndex: 50,
            minWidth: 200,
            maxHeight: 280,
            overflowY: 'auto',
            background: '#fff',
            border: '1px solid var(--color-border-default, #ececf2)',
            borderRadius: 6,
            boxShadow: '0 6px 16px rgba(0,0,0,0.08)',
            padding: 4,
          }}
        >
          {tokens.length === 0 ? (
            <p style={{ margin: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
              No tokens available in the current sample.
            </p>
          ) : (
            tokens.map((t) => (
              <button
                key={t}
                type="button"
                role="menuitem"
                data-testid={`merge-token-${t}`}
                onClick={() => {
                  onInsert(insertTokenAt(value, caret, t));
                  setOpen(false);
                }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  fontFamily: 'var(--font-mono, ui-monospace, monospace)',
                  fontSize: 12,
                  color: 'var(--color-brand-blue, #3b5bdb)',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: 4,
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-blue-50, #e8edff)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                {'{{'}{t}{'}}'}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
